/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Chat Context Types and Interfaces
 * 
 * Defines all TypeScript interfaces and types used by the chat system.
 */

import { LearningPathActionPlan } from "@shared/types/learning";
import { ActionPlanStatusResponseData } from '@shared/types/responses/learning';
import { ActionPlanResults } from '@shared/lib/action-plan-utils';
import { ToolCall, EntityLink } from '@shared/types/responses/streaming';
import { Plan, PlanTask } from '@shared/types/plan';

/**
 * Function that provides dynamic context for agent executions.
 * Called before each agent request to get current context state.
 * 
 * Example:
 * ```typescript
 * const contextProvider = () => ({
 *   activeProduct: product,
 *   repositoryUrl: repoUrl,
 *   userRole: 'admin'
 * });
 * ```
 */
export type AgentContextProvider = () => Record<string, any>;

export interface SubAgentCall {
  agentName: string;
  query: string;
  response?: string;
  status: 'started' | 'completed' | 'failed';
  timestamp: Date;
}

export interface ChatMessage {
  id?: string; // UUID from database
  uuid?: string; // Explicit UUID field
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  
  // UI-specific fields
  isStreaming?: boolean;
  subAgentCalls?: SubAgentCall[];
  
  // Rich content fields (from database JSON fields)
  actionPlan?: LearningPathActionPlan;
  toolCalls?: ToolCall[];
  entityLinks?: EntityLink[];
  
  // Metadata and execution state
  metadata?: {
    agentName?: string;
    model?: string;
    temperature?: number;
    tokenCount?: {
      input: number;
      output: number;
      total: number;
    };
    processingTime?: number;
    [key: string]: any;
  };
  
  executionState?: {
    isExecuting: boolean;
    isExecuted: boolean;
    executionId: string | null;
    executionStatus: ActionPlanStatusResponseData | null;
    executionResults: ActionPlanResults | null;
  };
}

export interface ChatState {
  // UI State
  isExpanded: boolean;
  sidebarWidth: number;
  isLoading: boolean;
  
  // Chat State
  messages: ChatMessage[];
  selectedAgent?: string;
  isExecuting: boolean; // Whether current session has active execution
  
  // Database Session State
  dbSessionId?: number;
  sessionKey?: string;
  sessionTitle?: string | null;
  
  // Honcho Integration State
  workspaceId?: string;
  honchoSessionId?: string;
  peerId?: string;
  isInitialized: boolean;
  
  // Execution State
  currentExecutionId?: string;
  canTerminateExecution?: boolean;
  
  // Plan State
  plan?: Plan | null;
}

export type ChatAction =
  | { type: 'SET_EXPANDED'; payload: boolean }
  | { type: 'SET_SIDEBAR_WIDTH'; payload: number }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_EXECUTING'; payload: boolean }
  | { type: 'SET_SELECTED_AGENT'; payload: string }
  | { type: 'ADD_MESSAGE'; payload: ChatMessage }
  | { type: 'SET_MESSAGES'; payload: ChatMessage[] }
  | { type: 'UPDATE_MESSAGE'; payload: { messageId: string; updates: Partial<ChatMessage> } }
  | { type: 'UPDATE_MESSAGE_CONTENT'; payload: { messageId: string; content: string; isIncremental?: boolean } }
  | { type: 'ADD_TOOL_CALL'; payload: { messageId: string; toolCall: ToolCall } }
  | { type: 'UPDATE_TOOL_CALL'; payload: { messageId: string; toolCallId: string; updates: Partial<ToolCall> } }
  | { type: 'ADD_ENTITY_LINK'; payload: { messageId: string; entityLink: EntityLink } }
  | { type: 'ADD_SUB_AGENT_CALL'; payload: { messageId: string; subAgentCall: SubAgentCall } }
  | { type: 'UPDATE_SUB_AGENT_CALL'; payload: { messageId: string; agentName: string; updates: Partial<SubAgentCall> } }
  | { type: 'UPDATE_ALL_SUB_AGENT_CALLS'; payload: { messageId: string; updates: Partial<SubAgentCall> } }
  | { type: 'SET_DB_SESSION'; payload: { dbSessionId: number; sessionKey: string; sessionTitle?: string | null } }
  | { type: 'SET_SESSION_TITLE'; payload: string }
  | { type: 'SET_HONCHO_STATE'; payload: { workspaceId?: string; honchoSessionId?: string; peerId?: string } }
  | { type: 'SET_INITIALIZED'; payload: boolean }
  | { type: 'UPDATE_MESSAGE_EXECUTION_STATE'; payload: { messageId: string; executionState: Partial<ChatMessage['executionState']> } }
  | { type: 'SET_EXECUTION_STATE'; payload: { executionId?: string; canTerminate?: boolean } }
  | { type: 'SET_PLAN'; payload: Plan | null }
  | { type: 'UPDATE_PLAN_TASK'; payload: { taskId: string; updates: Partial<PlanTask> } }
  | { type: 'RESET_CHAT' };

export interface ChatContextType {
  state: ChatState;
  dispatch: React.Dispatch<ChatAction>;
  // Actions
  setExpanded: (expanded: boolean) => void;
  setSidebarWidth: (width: number) => void;
  setSelectedAgent: (agentName: string) => void;
  addMessage: (message: ChatMessage, sessionId?: number) => Promise<any>;
  loadMessages: (sessionId?: number) => Promise<void>;
  initializeHoncho: () => Promise<void>;
  resetChat: () => Promise<void>;
  loadSession: (sessionId: number) => Promise<void>;
  loadSessionByKey: (sessionKey: string) => Promise<void>;
  setIsLoading: (loading: boolean) => void;
  updateSessionTitle: (title: string) => void;
  updateMessageExecutionState: (messageId: string, executionState: Partial<ChatMessage['executionState']>) => void;
  executeAgentWithStreaming: (agentName: string, message: string, fileAttachment?: File) => Promise<string>;
  terminateCurrentExecution: (reason?: string) => Promise<boolean>;
  startNewSession: () => Promise<any>;
  createSessionWithContext: (title: string, context: Record<string, any>, metadata?: Record<string, any>) => Promise<any>;
  /**
   * Get current agent context from the provider.
   * Returns an empty object if no context provider is configured.
   */
  getAgentContext: () => Record<string, any>;
}

export const initialState: ChatState = {
  isExpanded: false,
  sidebarWidth: 320,
  isLoading: false,
  messages: [],
  isInitialized: false,
  isExecuting: false,
  plan: null,
  // No session by default - will be created when user sends first message
};
